/**
 * SpryBoard Data Layer (SpryWorkspace Backend)
 *
 * Replaces the legacy Nextcloud/Firebase/api.sprymeeting.com data layer.
 * Now uses spryworkspace-backend REST API via axios (api/backend.ts).
 * Encryption is no longer used for collaboration (plaintext server-relay).
 */
import {
  generateEncryptionKey,
} from "@excalidraw/excalidraw/data/encryption";
import { restore } from "@excalidraw/excalidraw/data/restore";
import { isInvisiblySmallElement } from "@excalidraw/element";
import { isInitializedImageElement } from "@excalidraw/element";
import { t } from "@excalidraw/excalidraw/i18n";
import { bytesToHexString } from "@excalidraw/common";

import type { UserIdleState } from "@excalidraw/common";
import type { ImportedDataState } from "@excalidraw/excalidraw/data/types";
import type { SceneBounds } from "@excalidraw/element";
import type {
  ExcalidrawElement,
  FileId,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  BinaryFileData,
  BinaryFiles,
  SocketId,
} from "@excalidraw/excalidraw/types";
import type { MakeBrand } from "@excalidraw/common/utility-types";

import {
  DELETED_ELEMENT_TIMEOUT,
  FILE_UPLOAD_MAX_BYTES,
  ROOM_ID_BYTES,
} from "../app_constants";

import { saveFilesToBackend } from "./RestBackend";

import type { WS_SUBTYPES } from "../app_constants";
import {
  spryboardAPI,
  boardAPI,
  extractBoardFromResponse,
  extractSpryboardSaveFromResponse,
} from "../api/backend";

// =============================================================================
// Types
// =============================================================================

export type SyncableExcalidrawElement = OrderedExcalidrawElement &
  MakeBrand<"SyncableExcalidrawElement">;

export type SocketUpdateDataSource = {
  SCENE_INIT: {
    type: "SCENE_INIT";
    payload: { elements: readonly ExcalidrawElement[] };
  };
  SCENE_UPDATE: {
    type: "SCENE_UPDATE";
    payload: { elements: readonly ExcalidrawElement[] };
  };
  MOUSE_LOCATION: {
    type: "MOUSE_LOCATION";
    payload: {
      socketId: SocketId;
      pointer: { x: number; y: number };
      button: "down" | "up";
      selectedElementIds: AppState["selectedElementIds"];
      username: string;
    };
  };
  IDLE_STATUS: {
    type: "IDLE_STATUS";
    payload: {
      socketId: SocketId;
      userState: UserIdleState;
      username: string;
    };
  };
  USER_VISIBLE_SCENE_BOUNDS: {
    type: "USER_VISIBLE_SCENE_BOUNDS";
    payload: {
      socketId: SocketId;
      username: string;
      sceneBounds: SceneBounds;
    };
  };
};

export type SocketUpdateData = SocketUpdateDataSource[keyof SocketUpdateDataSource];

// =============================================================================
// Element Filtering
// =============================================================================

export const isSyncableElement = (
  element: OrderedExcalidrawElement,
): element is SyncableExcalidrawElement => {
  if (element.isDeleted) {
    if (element.updated > Date.now() - DELETED_ELEMENT_TIMEOUT) {
      return true;
    }
    return false;
  }
  return !isInvisiblySmallElement(element);
};

export const getSyncableElements = (
  elements: readonly OrderedExcalidrawElement[],
) =>
  elements.filter((element) =>
    isSyncableElement(element),
  ) as SyncableExcalidrawElement[];

// =============================================================================
// Room / Collaboration Link Helpers
// =============================================================================

const generateRoomId = async () => {
  const buffer = new Uint8Array(ROOM_ID_BYTES);
  window.crypto.getRandomValues(buffer);
  return bytesToHexString(buffer);
};

const RE_COLLAB_LINK = /^#room=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/;

export const isCollaborationLink = (link: string) => {
  const hash = new URL(link).hash;
  return RE_COLLAB_LINK.test(hash);
};

/**
 * Active board context — set when opening a board from SpryDrive or creating new.
 * Used by auto-save and collaboration to know which backend entity to target.
 */
let activeBoardId: string | null = null;
let activeDriveFileId: string | null = null;
let activeBoardTitle: string = "Untitled Board";
let activeBoardData: ImportedDataState | null = null;
let activeBoardCanEdit: boolean | null = null;
let activeBoardAccessRole: string | null = null;
let isInitialRouteResolving = false;
let hasInitialRouteResolved = false;
const uploadedFileIds = new Set<FileId>();

export const setActiveBoardContext = (ctx: {
  boardId?: string | null;
  driveFileId?: string | null;
  title?: string;
  initialData?: ImportedDataState | null;
  canEdit?: boolean | null;
  accessRole?: string | null;
}) => {
  if (ctx.boardId !== undefined) activeBoardId = ctx.boardId;
  if (ctx.driveFileId !== undefined) activeDriveFileId = ctx.driveFileId;
  if (ctx.title !== undefined) activeBoardTitle = ctx.title;
  if (ctx.initialData !== undefined) activeBoardData = ctx.initialData;
  if (ctx.canEdit !== undefined) activeBoardCanEdit = ctx.canEdit;
  if (ctx.accessRole !== undefined) activeBoardAccessRole = ctx.accessRole;
};

export const getActiveBoardContext = () => ({
  boardId: activeBoardId,
  driveFileId: activeDriveFileId,
  title: activeBoardTitle,
  canEdit: activeBoardCanEdit,
  accessRole: activeBoardAccessRole,
});

export type BoardBootstrapState = {
  isInitialRouteResolving: boolean;
  hasInitialRouteResolved: boolean;
};

export const setBoardBootstrapState = (state: Partial<BoardBootstrapState>) => {
  if (state.isInitialRouteResolving !== undefined) {
    isInitialRouteResolving = state.isInitialRouteResolving;
  }
  if (state.hasInitialRouteResolved !== undefined) {
    hasInitialRouteResolved = state.hasInitialRouteResolved;
  }
};

export const getBoardBootstrapState = (): BoardBootstrapState => ({
  isInitialRouteResolving,
  hasInitialRouteResolved,
});

export const resetBoardAutoCreateState = () => {
  if (autoSaveTimer) {
    clearTimeout(autoSaveTimer);
    autoSaveTimer = null;
  }
  autoCreateBoardPromise = null;
};

const normalizeAppStateForRestore = (appState: any) => {
  const normalized = { ...(appState || {}) } as Record<string, any>;
  const collaborators = normalized.collaborators;
  if (collaborators instanceof Map) {
    normalized.collaborators = collaborators;
  } else if (Array.isArray(collaborators)) {
    normalized.collaborators = new Map(collaborators);
  } else {
    normalized.collaborators = new Map();
  }
  return normalized;
};

const sanitizeAppStateForPersistence = (
  appState: Partial<AppState>,
): Partial<AppState> => {
  const sanitized = { ...(appState || {}) } as Record<string, unknown>;
  // Non-serializable and runtime-only collaborator map must not be persisted as plain object.
  delete sanitized.collaborators;
  return sanitized as Partial<AppState>;
};

const dataURLToUint8Array = (dataURL: string) => {
  const commaIndex = dataURL.indexOf(",");
  if (commaIndex < 0) {
    throw new Error("Invalid image data");
  }
  const header = dataURL.slice(0, commaIndex);
  const payload = dataURL.slice(commaIndex + 1);

  if (!header.includes(";base64")) {
    return new TextEncoder().encode(decodeURIComponent(payload));
  }

  const binary = window.atob(payload);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

export const getCollaborationLinkData = async (link: string) => {
  const hash = new URL(link).hash;
  const match = hash.match(RE_COLLAB_LINK);
  if (match && match[2].length !== 22) {
    window.alert(t("alerts.invalidEncryptionKey"));
    return null;
  }
  return match ? { roomId: match[1], roomKey: match[2] } : null;
};

export const generateCollaborationLinkData = async () => {
  const roomId = activeBoardId || (await generateRoomId());
  const roomKey = await generateEncryptionKey();

  if (!roomKey) {
    throw new Error("Couldn't generate room key");
  }

  return { roomId, roomKey };
};

export const getCollaborationLink = (data: {
  roomId: string;
  roomKey: string;
}) => {
  return `${window.location.origin}/#room=${data.roomId},${data.roomKey}`;
};

// =============================================================================
// Scene Load / Save
// =============================================================================

export const loadScene = async (
  id: string | null,
  privateKey: string | null,
  localDataState: ImportedDataState | undefined | null,
) => {
  let data;

  // If we have pre-loaded board data from SpryDrive, use it
  if (activeBoardData) {
    console.log("[SpryBoard] Using pre-loaded board data for loadScene");
    const normalizedInitialData = {
      ...activeBoardData,
      appState: normalizeAppStateForRestore((activeBoardData as any)?.appState),
    };
    data = restore(
      normalizedInitialData,
      localDataState?.appState,
      localDataState?.elements,
      {
        repairBindings: true,
        refreshDimensions: false,
        deleteInvisibleElements: true,
      },
    );
  } else if (id != null && activeBoardId) {
    // Load from SpryWorkspace backend by board ID
    try {
      const response = await boardAPI.getBoard(id, { useDrive: true });
      const boardPayload = extractBoardFromResponse(response);
      const boardData = boardPayload?.data;
      if (boardData) {
        data = restore(
          {
            elements: boardData.elements || [],
            appState: normalizeAppStateForRestore(boardData.appState || {}),
            files: boardData.files || {},
          },
          localDataState?.appState,
          localDataState?.elements,
          {
            repairBindings: true,
            refreshDimensions: false,
            deleteInvisibleElements: true,
          },
        );
      }
    } catch (err) {
      console.error("[SpryBoard] Failed to load board from backend:", err);
    }
  }

  if (!data) {
    data = restore(localDataState || null, null, null, {
      repairBindings: true,
      deleteInvisibleElements: true,
    });
  }

  return {
    elements: data.elements,
    appState: data.appState,
    files: data.files,
  };
};

// =============================================================================
// Export to Backend (shareable link)
// =============================================================================

type ExportToBackendResult =
  | { url: null; errorMessage: string }
  | { url: string; errorMessage: null };

export const exportToBackend = async (
  elements: readonly ExcalidrawElement[],
  appState: Partial<AppState>,
  files: BinaryFiles,
): Promise<ExportToBackendResult> => {
  try {
    // Save current board to SpryDrive
    const boardData = {
      elements,
      appState: sanitizeAppStateForPersistence(appState),
      files,
    };

    const filename = activeBoardTitle || "Shared Board";

    const response = await spryboardAPI.save({
      filename,
      boardData,
      fileId: activeDriveFileId || undefined,
      boardId: activeBoardId || undefined,
    });

    const { fileId, boardId } = extractSpryboardSaveFromResponse(response);

    if (fileId) {
      // Update active context
      if (boardId) activeBoardId = boardId;
      if (fileId) activeDriveFileId = fileId;

      const url = `${window.location.origin}/?boardId=${boardId || ""}&fileId=${fileId}`;
      return { url, errorMessage: null };
    }

    return { url: null, errorMessage: t("alerts.couldNotCreateShareableLink") };
  } catch (error: any) {
    console.error(error);
    return { url: null, errorMessage: t("alerts.couldNotCreateShareableLink") };
  }
};

// =============================================================================
// Auto-save to SpryDrive
// =============================================================================

let autoSaveTimer: ReturnType<typeof setTimeout> | null = null;
let autoCreateBoardPromise: Promise<boolean> | null = null;
const AUTO_SAVE_DEBOUNCE_MS = 3000;

const isNonPersistedExternalUrl = () => {
  const hash = window.location.hash || "";
  return (
    /^#room=/.test(hash) ||
    /^#json=/.test(hash) ||
    /^#url=/.test(hash)
  );
};

const hasBoardOrDriveSearchParams = () => {
  const searchParams = new URLSearchParams(window.location.search);
  return !!(searchParams.get("boardId") || searchParams.get("fileId"));
};

const ensureActiveBoardForAutoSave = async (boardData: {
  elements: readonly ExcalidrawElement[];
  appState: Partial<AppState>;
  files: BinaryFiles;
}) => {
  if (activeBoardId || activeDriveFileId) {
    return true;
  }

  if (isInitialRouteResolving && !hasInitialRouteResolved) {
    return false;
  }

  // Don't implicitly create boards for external/shared scenes.
  // Also avoid creating a new board while an existing board/file URL is still loading.
  if (isNonPersistedExternalUrl() || hasBoardOrDriveSearchParams()) {
    return false;
  }

  if (!autoCreateBoardPromise) {
    autoCreateBoardPromise = (async () => {
      try {
        const createResponse = await boardAPI.createBoard({
          title: activeBoardTitle || "Untitled Board",
          data: boardData,
        });
        const createdBoard = extractBoardFromResponse(createResponse);

        if (!createdBoard?.id) {
          return false;
        }

        activeBoardId = createdBoard.id;
        activeDriveFileId = createdBoard.driveFileId || null;
        activeBoardTitle = createdBoard.title || activeBoardTitle || "Untitled Board";

        const url = new URL(window.location.href);
        url.searchParams.set("boardId", activeBoardId);
        if (activeDriveFileId) {
          url.searchParams.set("fileId", activeDriveFileId);
        } else {
          url.searchParams.delete("fileId");
        }
        window.history.replaceState(
          {},
          document.title,
          `${url.pathname}${url.search}${url.hash}`,
        );

        return true;
      } catch (error) {
        console.warn("[SpryBoard] Auto-create board on first edit failed:", error);
        return false;
      } finally {
        autoCreateBoardPromise = null;
      }
    })();
  }

  return autoCreateBoardPromise;
};

export const triggerAutoSave = (
  elements: readonly ExcalidrawElement[],
  appState: Partial<AppState>,
  files: BinaryFiles,
) => {
  if (autoSaveTimer) {
    clearTimeout(autoSaveTimer);
  }

  autoSaveTimer = setTimeout(async () => {
    const boardData = {
      elements,
      appState: sanitizeAppStateForPersistence(appState),
      files,
    };
    const hasTarget = await ensureActiveBoardForAutoSave(boardData);
    if (!hasTarget) {
      return;
    }

    try {
      // Upload any new images to the backend first
      const imageElements = elements.filter(isInitializedImageElement);
      const filesToUpload: Map<FileId, BinaryFileData> = new Map();
      
      for (const element of imageElements) {
        const fileData = files[element.fileId];
        if (fileData && !uploadedFileIds.has(element.fileId)) {
          filesToUpload.set(element.fileId, fileData);
        }
      }

      if (filesToUpload.size > 0) {
        const prefix = activeBoardId || activeDriveFileId || 'board';
        const filesForUpload = Array.from(filesToUpload.entries()).map(
          ([id, fileData]) => {
            const buffer = dataURLToUint8Array(fileData.dataURL);

            if (buffer.byteLength > FILE_UPLOAD_MAX_BYTES) {
              throw new Error(
                t("errors.fileTooBig", {
                  maxSize: `${Math.trunc(FILE_UPLOAD_MAX_BYTES / 1024 / 1024)}MB`,
                }),
              );
            }

            return { id, buffer };
          },
        );
        
        const { savedFiles, erroredFiles } = await saveFilesToBackend({
          prefix,
          files: filesForUpload,
        });

        // Mark uploaded files
        for (const [fileId] of savedFiles) {
          uploadedFileIds.add(fileId);
        }

        if (erroredFiles.size > 0) {
          console.warn('[SpryBoard] Some images failed to upload:', [...erroredFiles.keys()]);
        }
      }

      if (activeDriveFileId) {
        await spryboardAPI.autoSave(activeDriveFileId, {
          boardData,
          filename: activeBoardTitle,
        });
      } else if (activeBoardId) {
        const response = await boardAPI.updateBoard(activeBoardId, {
          data: boardData,
        });
        const updatedBoard = extractBoardFromResponse(response);
        if (updatedBoard?.driveFileId && !activeDriveFileId) {
          activeDriveFileId = updatedBoard.driveFileId;
        }
      }
    } catch (err) {
      console.warn("[SpryBoard] Auto-save failed:", err);
    }
  }, AUTO_SAVE_DEBOUNCE_MS);
};
